import ApiService from "@/core/services/ApiService";
import ChatStreamService, { SseEvent } from "@/core/services/ChatStreamService";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

export interface ChatConversationSummary {
  id: number;
  title: string | null;
  model: string | null;
  last_message_at: string | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: string | number;
  created_at: string;
  updated_at: string;
}

export interface ChatToolCall {
  id: string;
  name: string;
  arguments: string;
  args_obj?: Record<string, unknown>;
  status?: "running" | "done" | "error";
  latency_ms?: number;
}

export interface ChatMessage {
  id: number | string;
  conversation_id: number;
  role: "user" | "assistant" | "tool";
  content: string | null;
  tool_calls?: ChatToolCall[] | null;
  tool_call_id?: string | null;
  tool_name?: string | null;
  error?: string | null;
  created_at?: string;
  _streaming?: boolean;
}

let streamService: ChatStreamService | null = null;

@Module
export default class ChatModule extends VuexModule {
  conversations: ChatConversationSummary[] = [];
  currentConversationId: number | null = null;
  messages: ChatMessage[] = [];
  isStreaming = false;
  loadingConversations = false;
  loadingMessages = false;
  streamError: string | null = null;

  get sortedConversations(): ChatConversationSummary[] {
    return [...this.conversations].sort((a, b) => {
      const at = a.last_message_at || a.updated_at || a.created_at;
      const bt = b.last_message_at || b.updated_at || b.created_at;
      return new Date(bt).getTime() - new Date(at).getTime();
    });
  }

  @Mutation
  SET_CONVERSATIONS(list: ChatConversationSummary[]): void {
    this.conversations = list;
  }

  @Mutation
  ADD_OR_UPDATE_CONVERSATION(conv: ChatConversationSummary): void {
    const i = this.conversations.findIndex((c) => c.id === conv.id);
    if (i >= 0) this.conversations.splice(i, 1, { ...this.conversations[i], ...conv });
    else this.conversations.unshift(conv);
  }

  @Mutation
  REMOVE_CONVERSATION(id: number): void {
    this.conversations = this.conversations.filter((c) => c.id !== id);
    if (this.currentConversationId === id) {
      this.currentConversationId = null;
      this.messages = [];
    }
  }

  @Mutation
  SET_CURRENT(id: number | null): void {
    this.currentConversationId = id;
    if (id === null) this.messages = [];
  }

  @Mutation
  SET_MESSAGES(msgs: ChatMessage[]): void {
    this.messages = msgs;
  }

  @Mutation
  APPEND_MESSAGE(msg: ChatMessage): void {
    this.messages.push(msg);
  }

  @Mutation
  UPDATE_STREAMING_ASSISTANT(payload: { content?: string; toolCall?: ChatToolCall; toolCallUpdate?: { id: string; ok: boolean; latency_ms: number } }): void {
    const last = this.messages[this.messages.length - 1];
    if (!last || last.role !== "assistant" || !last._streaming) return;

    if (payload.content !== undefined) {
      last.content = (last.content || "") + payload.content;
    }
    if (payload.toolCall) {
      last.tool_calls = last.tool_calls || [];
      last.tool_calls.push({ ...payload.toolCall, status: "running" });
    }
    if (payload.toolCallUpdate) {
      const tc = (last.tool_calls || []).find((t) => t.id === payload.toolCallUpdate!.id);
      if (tc) {
        tc.status = payload.toolCallUpdate.ok ? "done" : "error";
        tc.latency_ms = payload.toolCallUpdate.latency_ms;
      }
    }
  }

  @Mutation
  FINALIZE_STREAMING_ASSISTANT(payload: { messageId: number | null }): void {
    const last = this.messages[this.messages.length - 1];
    if (!last || last.role !== "assistant") return;
    last._streaming = false;
    if (payload.messageId !== null) last.id = payload.messageId;
  }

  @Mutation
  SET_IS_STREAMING(v: boolean): void {
    this.isStreaming = v;
  }

  @Mutation
  SET_LOADING_CONVERSATIONS(v: boolean): void {
    this.loadingConversations = v;
  }

  @Mutation
  SET_LOADING_MESSAGES(v: boolean): void {
    this.loadingMessages = v;
  }

  @Mutation
  SET_STREAM_ERROR(v: string | null): void {
    this.streamError = v;
  }

  @Action
  async fetchConversations(): Promise<void> {
    this.context.commit("SET_LOADING_CONVERSATIONS", true);
    try {
      const { data } = await ApiService.post("admin/chat/conversations", {} as never);
      if (data?.status === 0) {
        this.context.commit("SET_CONVERSATIONS", data.data || []);
      }
    } finally {
      this.context.commit("SET_LOADING_CONVERSATIONS", false);
    }
  }

  @Action
  async selectConversation(id: number): Promise<void> {
    this.context.commit("SET_CURRENT", id);
    this.context.commit("SET_LOADING_MESSAGES", true);
    try {
      const { data } = await ApiService.post(`admin/chat/conversations/${id}`, {} as never);
      if (data?.status === 0) {
        const rawMsgs: Array<Record<string, unknown>> = data.data?.messages || [];
        const msgs: ChatMessage[] = rawMsgs.map((m) => ({
          id: m.id as number,
          conversation_id: m.conversation_id as number,
          role: m.role as ChatMessage["role"],
          content: (m.content as string) ?? null,
          tool_calls: (m.tool_calls as ChatToolCall[]) || null,
          tool_call_id: (m.tool_call_id as string) || null,
          tool_name: (m.tool_name as string) || null,
          error: (m.error as string) || null,
          created_at: m.created_at as string,
        }));
        this.context.commit("SET_MESSAGES", msgs);
      }
    } finally {
      this.context.commit("SET_LOADING_MESSAGES", false);
    }
  }

  @Action
  async createConversation(): Promise<number | null> {
    const { data } = await ApiService.post("admin/chat/conversations/new", {} as never);
    if (data?.status === 0 && data.data) {
      const conv = data.data as ChatConversationSummary;
      this.context.commit("ADD_OR_UPDATE_CONVERSATION", conv);
      this.context.commit("SET_CURRENT", conv.id);
      this.context.commit("SET_MESSAGES", []);
      return conv.id;
    }
    return null;
  }

  @Action
  async renameConversation(payload: { id: number; title: string }): Promise<void> {
    const { data } = await ApiService.post(`admin/chat/conversations/${payload.id}/rename`, { title: payload.title } as never);
    if (data?.status === 0 && data.data) {
      this.context.commit("ADD_OR_UPDATE_CONVERSATION", data.data);
    }
  }

  @Action
  async deleteConversation(id: number): Promise<void> {
    await ApiService.post(`admin/chat/conversations/${id}/delete`, {} as never);
    this.context.commit("REMOVE_CONVERSATION", id);
  }

  @Action
  async sendMessage(payload: { message: string }): Promise<void> {
    let conversationId = this.currentConversationId;
    if (!conversationId) {
      conversationId = await this.context.dispatch("createConversation");
      if (!conversationId) return;
    }

    // Append user message optimistically
    this.context.commit("APPEND_MESSAGE", {
      id: `tmp-user-${Date.now()}`,
      conversation_id: conversationId,
      role: "user",
      content: payload.message,
      created_at: new Date().toISOString(),
    } as ChatMessage);

    // Placeholder streaming assistant message
    this.context.commit("APPEND_MESSAGE", {
      id: `tmp-asst-${Date.now()}`,
      conversation_id: conversationId,
      role: "assistant",
      content: "",
      tool_calls: [],
      _streaming: true,
      created_at: new Date().toISOString(),
    } as ChatMessage);

    this.context.commit("SET_IS_STREAMING", true);
    this.context.commit("SET_STREAM_ERROR", null);

    streamService = new ChatStreamService();
    let finalMessageId: number | null = null;

    await streamService.stream(conversationId, payload.message, (event: SseEvent) => {
      switch (event.type) {
        case "text_delta":
          this.context.commit("UPDATE_STREAMING_ASSISTANT", { content: event.delta });
          break;
        case "tool_call_start":
          this.context.commit("UPDATE_STREAMING_ASSISTANT", {
            toolCall: {
              id: event.tool_call_id,
              name: event.tool,
              arguments: JSON.stringify(event.args),
              args_obj: event.args,
              status: "running",
            },
          });
          break;
        case "tool_call_end":
          this.context.commit("UPDATE_STREAMING_ASSISTANT", {
            toolCallUpdate: { id: event.tool_call_id, ok: event.ok, latency_ms: event.latency_ms },
          });
          break;
        case "done":
          finalMessageId = event.assistant_message_id;
          if (event.title) {
            this.context.commit("ADD_OR_UPDATE_CONVERSATION", {
              id: event.conversation_id,
              title: event.title,
              last_message_at: new Date().toISOString(),
            } as ChatConversationSummary);
          }
          break;
        case "error":
          this.context.commit("SET_STREAM_ERROR", event.message);
          break;
        case "close":
          break;
      }
    });

    this.context.commit("FINALIZE_STREAMING_ASSISTANT", { messageId: finalMessageId });
    this.context.commit("SET_IS_STREAMING", false);
    streamService = null;

    // Refresh conversation summary for token/cost updates
    this.context.dispatch("fetchConversations");
  }

  @Action
  abortStream(): void {
    if (streamService) {
      streamService.abort();
    }
  }
}
